import {
	ContractBondSubtype,
	ContractSuretyType,
	FileType,
	isCommercialSuretyBondType,
	isContractCategoryBondType,
	type NaicsCodeId,
	type SuretyBondFormType,
	type UploadedFile,
} from "@inrev/common";
import { useFetchBondFormTemplates } from "@portal/api/bond-request";
import { useFileClassificationPipeline } from "@portal/api/bond-request/agent";
import {
	buildCommercialBondTypeOptions,
	LEGACY_BOND_TYPE_OPTIONS,
} from "@portal/components/features/bond-request-submission/bondRequestBondTypeOptions";
import { resolveContractSuretyType } from "@portal/components/features/bond-request-submission/bondRequestSubmissionBondTypeUtils";
import { BidToFinalFields } from "@portal/components/features/bond-request-submission/steps/BidToFinalFields";
import { type DocumentFile } from "@portal/components/features/bond-request-submission/steps/DocumentFileRow";
import { DocumentUploadSection } from "@portal/components/features/bond-request-submission/steps/DocumentUploadSection";
import {
	ContractDamagesField,
	ContractHazardsField,
} from "@portal/components/features/contract-project";
import {
	BondFormSelectInputSection,
	FormComponent,
	FormFieldError,
	FormFieldGroup,
	FormRow,
	FormSection,
} from "@portal/components/form";
import {
	BOND_FORM_HEADER,
	BOND_FORM_UPLOAD_ONLY_TOOLTIP,
	BondFormSectionHeader,
} from "@portal/components/form/BondFormSectionHeader";
import {
	FormCurrencyComponent,
	FormCurrencyPercentComponent,
	FormDateComponent,
	FormDropdownComponent,
	FormInputComponent,
	FormNumberComponent,
	FormStateDropdownComponent,
	FormYesNoComponent,
} from "@portal/components/form/fieldLayouts";
import { FormNAICSCodeSelect, FormTextArea } from "@portal/components/form/fields";
import { FormBondFormSelect } from "@portal/components/form/fields/FormBondFormSelect";
import { FormComment } from "@portal/components/form/fields/FormComment";
import { DropdownInput } from "@portal/components/form/inputs/DropdownInput";
import { FileUpload } from "@portal/components/ui/Upload";
import { useFeatureFlag } from "@portal/hooks/useFeatureFlag";
import { useIsCommercialSuretyEnabled } from "@portal/hooks/useIsCommercialSuretyEnabled";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { useContext, useEffect, useMemo, useRef } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { PROJECT_NAME_TOOLTIP } from "../../../../../../constants";
import type { BondFormData } from "../../../../../../types";
import {
	getAllowedBondFormUploadTypes,
	getFilteredBondFormTemplates,
} from "../../../../../shared/bond/bondUtils";
import { BondRequestCreateContext, type PrincipalCompanyOption } from "../../BondRequestCreateView";
import type { BondRequestFormValues } from "../../schema";
import { getDefaultBondRequestFormData } from "../../schema";

const finalBondFormTypeOptions = [
	{ value: "pnp", label: "Performance and Payment" },
	{ value: "performance", label: "Performance" },
	{ value: "payment", label: "Payment" },
];

const EMPTY_TEMPLATES: never[] = [];
const NO_TYPE_LABELS: Partial<Record<SuretyBondFormType, string>> = {};

const CommercialBondFields = () => {
	const formMethods = useFormContext<BondRequestFormValues>();
	const { control } = formMethods;
	const bondForm = useWatch({ control, name: "commercialBond.bondForm" });
	const bondFormSelected = (bondForm?.upload?.files.length ?? 0) > 0;

	return (
		<>
			<FormRow>
				<FormCurrencyComponent
					formMethods={formMethods}
					name="commercialBond.bondAmount"
					label="Bond Amount"
					testId="commercial-bond-amount"
				/>
				<FormDateComponent
					formMethods={formMethods}
					name="commercialBond.effectiveDate"
					label="Effective Date"
				/>
			</FormRow>
			<FormComponent
				formMethods={formMethods}
				name="commercialBond.submissionNarrative"
				label="Submission Narrative"
			>
				<FormTextArea
					control={control}
					name="commercialBond.submissionNarrative"
					className="h-[110px]"
					testId="submission-narrative-input"
					placeholder="Include relevant amounts and dates, special requirements, or other context that may help process this request..."
				/>
			</FormComponent>
			<div className="flex flex-col gap-3" data-testid="commercial-bond-form">
				<BondFormSectionHeader
					tooltipId="full-submit-commercial-bond-form"
					showTooltip={!bondFormSelected}
					tooltipContent={BOND_FORM_UPLOAD_ONLY_TOOLTIP}
					label={<span>{BOND_FORM_HEADER}</span>}
					className="gap-1.5"
					tooltipClassName="text-[16px]"
					tooltipMaxWidth={300}
					condensed={false}
				/>
				<FormBondFormSelect<BondRequestFormValues>
					name="commercialBond.bondForm"
					control={control}
					templates={EMPTY_TEMPLATES}
					typeLabels={NO_TYPE_LABELS}
					allowLibrary={false}
					id="commercialBondForm"
					compact
				/>
			</div>
		</>
	);
};

const useSyncBondToContract = (
	formMethods: ReturnType<typeof useFormContext<BondRequestFormValues>>,
) => {
	const onBondAmountBlur = () => {
		if (formMethods.getValues("contractAmountEdited")) return;
		const bondAmount = formMethods.getValues("quote.bondAmount");
		if (bondAmount) {
			formMethods.setValue("quote.contractAmount", bondAmount);
		}
	};

	const onContractAmountChange = () => {
		formMethods.setValue("contractAmountEdited", true);
	};

	return { onBondAmountBlur, onContractAmountChange };
};

export const BondStep = ({
	includeCommercialFields = false,
}: {
	includeCommercialFields?: boolean;
	formData?: unknown;
}) => {
	const { accountNaicsCodes, companies, bidBondPrincipalId } = useContext(BondRequestCreateContext);
	const formMethods = useFormContext<BondRequestFormValues>();
	const { control, formState } = formMethods;
	const isExtractionEnabled = useFeatureFlag("bond_request_extraction");
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();

	const isCommercialSuretyEnabled = useIsCommercialSuretyEnabled();

	const contractSuretyType = useWatch({ control, name: "quote.contractSuretyType" });
	const bondType = useWatch({ control, name: "bondType" });
	const contractBondType = useWatch({ control, name: "contractBondType" });
	const prevContractSuretyTypeRef = useRef(contractSuretyType);

	// Flows that cannot submit a commercial bond stay on the legacy contractSuretyType list
	// rather than showing contract categories without their commercial counterparts.
	const commercialBondTypeModelEnabled = includeCommercialFields && isCommercialSuretyEnabled;
	const isCommercialBond = commercialBondTypeModelEnabled && isCommercialSuretyBondType(bondType);
	const showContractBondType =
		commercialBondTypeModelEnabled && isContractCategoryBondType(bondType);
	const bondTypeOptions = useMemo(
		() =>
			commercialBondTypeModelEnabled
				? buildCommercialBondTypeOptions(true)
				: LEGACY_BOND_TYPE_OPTIONS,
		[commercialBondTypeModelEnabled],
	);

	const isBid = !isCommercialBond && contractSuretyType === ContractSuretyType.bid;
	const isFinal = !isCommercialBond && contractSuretyType === ContractSuretyType.final;
	const isB2F = !isCommercialBond && contractSuretyType === ContractSuretyType.bid_to_final;
	const hasType = isBid || isFinal || isB2F;

	const { onBondAmountBlur, onContractAmountChange } = useSyncBondToContract(formMethods);

	const { bondFormData, setBondFormData } = useContext(BondRequestCreateContext);

	const companyOptions = useMemo(() => {
		if (!companies) return [];
		return companies.map((c) => ({ value: c.name, label: c.name }));
	}, [companies]);

	const setPrincipal = (company: PrincipalCompanyOption) => {
		if (company.id) {
			formMethods.setValue(
				"quote.principal",
				{
					id: company.id,
					name: company.name,
				},
				{ shouldValidate: true },
			);
		} else {
			formMethods.setValue("quote.principal.name", company.name, { shouldValidate: true });
		}
	};

	useEffect(() => {
		if (!companies || companies.length === 0) return;

		if (isB2F) {
			const bidBondPrincipal = companies.find((c) => c.id === bidBondPrincipalId);
			if (bidBondPrincipal) {
				setPrincipal(bidBondPrincipal);
			}
			return;
		}

		if (companies.length === 1) {
			setPrincipal(companies[0]);
			return;
		}

		const currentValue = formMethods.getValues("quote.principal.name");
		if (!currentValue) {
			const primary = companies.find((c) => c.isPrimary);
			if (primary) {
				setPrincipal(primary);
			}
		}
	}, [companies, isB2F, bidBondPrincipalId]);

	const selectedPrincipalName = useWatch({
		control,
		name: "quote.principal.name",
	});

	useEffect(() => {
		if (!companies || !selectedPrincipalName) return;
		const company = companies.find((c) => c.name === selectedPrincipalName);
		if (company?.id) {
			const current = formMethods.getValues("quote.principal");
			if (current?.id !== company.id) {
				formMethods.setValue("quote.principal", {
					id: company.id,
					name: company.name,
				});
			}
		}
	}, [selectedPrincipalName, companies]);

	const filesController = useController({ control, name: "quote.files" });
	const attachmentsController = useController({
		control,
		name: "quote.additionalInfo.attachments",
	});

	const initialClassificationFilesRef = useRef<UploadedFile[] | undefined>(undefined);
	if (initialClassificationFilesRef.current === undefined) {
		initialClassificationFilesRef.current = [
			...(filesController.field.value ?? []),
			...(attachmentsController.field.value ?? []),
		];
	}
	const initialClassificationFiles = initialClassificationFilesRef.current;

	const classificationPipeline = useFileClassificationPipeline({
		initialFiles: initialClassificationFiles.length > 0 ? initialClassificationFiles : undefined,
	});

	useEffect(() => {
		if (
			prevContractSuretyTypeRef.current &&
			prevContractSuretyTypeRef.current !== contractSuretyType
		) {
			const principal = formMethods.getValues("quote.principal");
			const additionalInfo = formMethods.getValues("quote.additionalInfo");
			const project = formMethods.getValues("quote.project");
			const completionBond = formMethods.getValues("quote.completionBond");
			const tListingRequired = formMethods.getValues("quote.tListingRequired");
			const defaults = getDefaultBondRequestFormData(contractSuretyType);
			formMethods.setValue("quote", defaults.quote as BondRequestFormValues["quote"]);
			formMethods.setValue("quote.principal", principal);
			formMethods.setValue("quote.additionalInfo", additionalInfo);
			formMethods.setValue("quote.project", project);
			formMethods.setValue("quote.completionBond", completionBond);
			formMethods.setValue("quote.tListingRequired", tListingRequired);
			formMethods.setValue("contractAmountEdited", false);
		}
		prevContractSuretyTypeRef.current = contractSuretyType;
	}, [contractSuretyType, formMethods]);

	useEffect(() => {
		if (!commercialBondTypeModelEnabled) return;
		const resolved = resolveContractSuretyType(bondType, contractBondType);
		if (resolved && resolved !== formMethods.getValues("quote.contractSuretyType")) {
			formMethods.setValue("quote.contractSuretyType", resolved);
		}
	}, [bondType, contractBondType, formMethods, commercialBondTypeModelEnabled]);

	const filesOnChangeRef = useRef(filesController.field.onChange);
	filesOnChangeRef.current = filesController.field.onChange;
	const attachmentsOnChangeRef = useRef(attachmentsController.field.onChange);
	attachmentsOnChangeRef.current = attachmentsController.field.onChange;

	useEffect(() => {
		if (!isMinimalUIEnabled || !hasType) return;
		// quote.files only accepts the bond-type-specific document type (bid_package/contract);
		// files the backend classifies as anything else still need a home, so they fall back to
		// the unrestricted additionalInfo.attachments rather than failing schema validation.
		const expectedFileType = isBid ? FileType.bid_package : FileType.contract;
		const uploaded = classificationPipeline.files.flatMap((f) =>
			f.uploadedFile ? [{ ...f.uploadedFile, types: f.types }] : [],
		);
		const primaryDocs = uploaded.filter(
			(f) => f.types.length > 0 && f.types.every((t) => t === expectedFileType),
		);
		const otherDocs = uploaded.filter(
			(f) => f.types.length === 0 || f.types.some((t) => t !== expectedFileType),
		);
		filesOnChangeRef.current(primaryDocs);
		attachmentsOnChangeRef.current(otherDocs);
	}, [classificationPipeline.files, isMinimalUIEnabled, hasType, isBid]);

	const { bondFormTemplates } = useFetchBondFormTemplates();
	const filteredBondFormTemplates = useMemo(
		() => getFilteredBondFormTemplates(bondFormTemplates, contractSuretyType),
		[bondFormTemplates, contractSuretyType],
	);

	const allowedBondFormUploadTypes = useMemo(
		() => getAllowedBondFormUploadTypes(contractSuretyType),
		[contractSuretyType],
	);

	return (
		<div className="flex flex-col space-y-[44px] pt-[18px] pb-[125px] min-h-fit">
			{companies && !isB2F && (
				<FormFieldGroup>
					<PrincipalField
						formMethods={formMethods}
						companies={companies}
						companyOptions={companyOptions}
					/>
				</FormFieldGroup>
			)}

			{!isB2F && (
				<FormFieldGroup>
					<FormDropdownComponent
						formMethods={formMethods}
						name={commercialBondTypeModelEnabled ? "bondType" : "quote.contractSuretyType"}
						label="Bond Type"
						placeholder="Select one"
						options={bondTypeOptions}
						testId="bond-type-dropdown"
					/>
					{showContractBondType && (
						<div data-testid="contract-bond-type-dropdown">
							<FormDropdownComponent
								formMethods={formMethods}
								name="contractBondType"
								label="Contract Bond Type"
								placeholder="Select bond type"
								options={ContractBondSubtype.options()}
								testId="contract-bond-type-input"
							/>
						</div>
					)}

					{isCommercialBond && <CommercialBondFields />}

					{isBid && !isMinimalUIEnabled && (
						<>
							{!isExtractionEnabled && (
								<FormDateComponent
									formMethods={formMethods}
									name="quote.bidDate"
									label="Bid Date"
								/>
							)}
							<FormRow>
								<FormCurrencyComponent
									formMethods={formMethods}
									name="quote.bidAmount"
									label="Bid Amount"
								/>
								<FormCurrencyPercentComponent
									formMethods={formMethods}
									referenceSourceName="quote.bidAmount"
									currencyName="quote.bondAmount"
									percentName="quote.bondAmountPercentOfBid"
									amountTypeName="quote.bidBondAmountType"
									defaultAmountType="percent"
								/>
							</FormRow>
							<FormDropdownComponent
								formMethods={formMethods}
								name="quote.finalBondFormType"
								label="Final Bond Form"
								placeholder="Select one"
								options={finalBondFormTypeOptions}
							/>
						</>
					)}

					{isFinal && !isMinimalUIEnabled && (
						<FormRow>
							<FormCurrencyComponent
								formMethods={formMethods}
								name="quote.bondAmount"
								label="Bond Amount"
								onValueBlur={onBondAmountBlur}
							/>
							<FormCurrencyComponent
								formMethods={formMethods}
								name="quote.contractAmount"
								label="Contract Amount"
								onValueChange={onContractAmountChange}
							/>
						</FormRow>
					)}

					{isMinimalUIEnabled && hasType && (
						<FormInputComponent
							formMethods={formMethods}
							name="quote.project.name"
							label="Project Name"
							tooltip={PROJECT_NAME_TOOLTIP}
							testId="project-name-input"
						/>
					)}

					{isMinimalUIEnabled && isBid && (
						<FormCurrencyComponent
							formMethods={formMethods}
							name="quote.bidAmount"
							label="Bid Amount"
							testId="bid-amount-input"
						/>
					)}
				</FormFieldGroup>
			)}

			{isB2F && (
				<>
					<BidToFinalFields
						formMethods={formMethods}
						minimal={isMinimalUIEnabled}
						onBondAmountBlur={onBondAmountBlur}
						onContractAmountChange={onContractAmountChange}
						names={{
							actualBidAmount: "quote.actualBidAmount",
							secondBid: "quote.secondBid",
							bondAmount: "quote.bondAmount",
							contractAmount: "quote.contractAmount",
							actualBidDate: "quote.actualBidDate",
							contractDate: "quote.contractDate",
							tListingRequired: "quote.tListingRequired",
							projectName: "quote.project.name",
						}}
					/>

					{!isMinimalUIEnabled && (
						<>
							<FormSection
								header="Contract"
								subHeader="Please upload a copy of the contract"
								id="files"
								partial={formState.isSubmitted && (filesController.field.value ?? []).length === 0}
							>
								<FileUpload
									showTabs={false}
									value={filesController.field.value ?? []}
									onChange={filesController.field.onChange}
									onBlur={filesController.field.onBlur}
									enablePDFViewer={false}
									allowDelete={true}
									enableTypeSelection={false}
									allowedTypesAndLabels={{ [FileType.contract]: "Contract" as const }}
									maxTypesPerFile={1}
									showErrors={formState.isSubmitted}
									hideChecklist
									className="w-[450px]"
									analyticsContext="submission-upload"
									trackingPrefix="bond-request-page-upload"
								/>
							</FormSection>

							<BondFormSelectInputSection
								value={bondFormData}
								onChange={setBondFormData}
								templates={filteredBondFormTemplates}
								typeLabels={allowedBondFormUploadTypes}
								id="quote.bondForm"
								partial={
									formState.isSubmitted &&
									!bondFormData.templateId &&
									bondFormData.upload.files.length === 0
								}
							/>
						</>
					)}
				</>
			)}

			{!isB2F && hasType && !isMinimalUIEnabled && (
				<BondFormSelectInputSection
					value={bondFormData}
					onChange={setBondFormData}
					templates={filteredBondFormTemplates}
					typeLabels={allowedBondFormUploadTypes}
					id="quote.bondForm"
					partial={
						formState.isSubmitted &&
						!bondFormData.templateId &&
						bondFormData.upload.files.length === 0
					}
				/>
			)}

			{!isB2F && hasType && !isExtractionEnabled && (
				<FormSection header="Project" contentClassName="space-y-[35px] mt-[25px]">
					<ScopeOfWorkField formMethods={formMethods} accountNaicsCodes={accountNaicsCodes} />

					<FormFieldGroup>
						<FormInputComponent
							formMethods={formMethods}
							name="quote.project.description"
							label="Project Description"
						/>
						<FormRow>
							<FormInputComponent
								formMethods={formMethods}
								name="quote.project.address.city"
								label="Project City"
							/>
							<FormStateDropdownComponent
								formMethods={formMethods}
								name="quote.project.address.state"
								label="Project State"
								optionsClassName="max-h-[230px] overflow-y-auto"
							/>
						</FormRow>
					</FormFieldGroup>

					<FormRow>
						<FormNumberComponent
							formMethods={formMethods}
							name="quote.project.monthsToComplete"
							label="Time to Complete (months)"
							min={1}
						/>
						<FormNumberComponent
							formMethods={formMethods}
							name="quote.project.contractWarrantyMonths"
							label="Warranty Period (months)"
							min={0}
						/>
					</FormRow>

					<ContractDamagesField
						formMethods={formMethods}
						name="quote.project.contractDamages"
						label="Does the contract include any of the following damages?"
						subLabel="Check all that apply"
						tooltip="Damages are usually described in the contract but may only be described in the prime contract. Note: AIA contracts usually describe damages in Section 4.5 (A101, A503)."
						className="bg-surface-light border border-bc-strong"
					/>

					<ContractHazardsField
						formMethods={formMethods}
						name="quote.project.contractHazards"
						label="Do any of the following apply to the contract?"
						subLabel="Check all that apply"
						tooltip="If these attributes are present, they will be described in the contract."
						className="bg-surface-light border border-bc-strong"
					/>

					<CompletionBondField formMethods={formMethods} />

					<TreasuryListingField formMethods={formMethods} />

					<BondFileUploadSection
						topPadding
						isBid={isBid}
						filesController={filesController}
						isSubmitted={formState.isSubmitted}
					/>
				</FormSection>
			)}

			{!isB2F && hasType && isExtractionEnabled && !isMinimalUIEnabled && (
				<BondFileUploadSection
					isBid={isBid}
					filesController={filesController}
					isSubmitted={formState.isSubmitted}
				/>
			)}

			{hasType && isMinimalUIEnabled && (
				<MinimalFileUploadSection
					classificationPipeline={classificationPipeline}
					formMethods={formMethods}
					bondFormData={bondFormData}
					setBondFormData={setBondFormData}
					bondFormTemplates={filteredBondFormTemplates}
					allowedBondFormUploadTypes={allowedBondFormUploadTypes}
				/>
			)}
		</div>
	);
};

type PrincipalFieldProps = {
	formMethods: ReturnType<typeof useFormContext<BondRequestFormValues>>;
	companies: PrincipalCompanyOption[];
	companyOptions: { value: string; label: string }[];
};

// The Principal dropdown edits `quote.principal.name`, but the schema also requires
// `quote.principal.id` (backfilled from the selected company). Selecting a company sets both
// atomically so `id` is never missing while `name` is set, and we surface either field's error on
// the visible input — otherwise a missing `id` fails validation with no on-screen indication.
const PrincipalField = ({ formMethods, companies, companyOptions }: PrincipalFieldProps) => {
	const { control, setValue, formState } = formMethods;
	const selectedName = useWatch({ control, name: "quote.principal.name" });
	const principalError = formState.errors.quote?.principal;
	const errorMessage = principalError?.id?.message ?? principalError?.name?.message ?? "Required";
	const hasError = !!principalError?.id || !!principalError?.name;

	const handleChange = (name: string) => {
		const company = companies.find((c) => c.name === name);
		if (company?.id) {
			setValue(
				"quote.principal",
				{ id: company.id, name: company.name },
				{ shouldValidate: formState.isSubmitted },
			);
		} else {
			setValue("quote.principal.name", name, { shouldValidate: formState.isSubmitted });
		}
	};

	return (
		<FormComponent formMethods={formMethods} name="quote.principal.name" label="Principal">
			<div className="relative" data-testid="principal">
				<DropdownInput
					value={selectedName ?? ""}
					onChange={handleChange}
					options={companyOptions}
					placeholder="Select principal"
					error={hasError}
				/>
				{hasError && <FormFieldError error={{ type: "validate", message: errorMessage }} />}
			</div>
		</FormComponent>
	);
};

type ProjectQuestionFieldProps = {
	formMethods: ReturnType<typeof useFormContext<BondRequestFormValues>>;
	accountNaicsCodes?: NaicsCodeId[];
};

const ScopeOfWorkField = ({ formMethods, accountNaicsCodes }: ProjectQuestionFieldProps) => (
	<FormComponent
		formMethods={formMethods}
		name="quote.project.scopeOfWork"
		label="What type of work will be performed?"
	>
		{accountNaicsCodes?.length ? (
			<FormNAICSCodeSelect
				name="quote.project.scopeOfWork"
				control={formMethods.control}
				selectText="Select scopes of work"
				editText="Edit scopes of work"
				title="Select Scopes of Work"
				searchPlaceholder="Search scopes of work..."
				subsetFilters={[{ label: "Account specialties", subset: accountNaicsCodes }]}
				defaultSubsetIndex={0}
				allNAICSCodesLabel="All scopes of work"
			/>
		) : (
			<FormNAICSCodeSelect
				name="quote.project.scopeOfWork"
				control={formMethods.control}
				selectText="Select scopes of work"
				editText="Edit scopes of work"
				title="Select Scopes of Work"
				searchPlaceholder="Search scopes of work..."
			/>
		)}
	</FormComponent>
);

const CompletionBondField = ({
	formMethods,
}: {
	formMethods: ReturnType<typeof useFormContext<BondRequestFormValues>>;
}) => (
	<FormYesNoComponent
		formMethods={formMethods}
		name="quote.completionBond"
		label="Is the principal a developer and/or is this a completion bond?"
		tooltip="Completion bonds are performance guarantees where the Principal is responsible for financing the project. They include site bonds, subdivision bonds, right-of-way bonds, and takeover contracts."
		deselectable
	/>
);

const TreasuryListingField = ({
	formMethods,
}: {
	formMethods: ReturnType<typeof useFormContext<BondRequestFormValues>>;
}) => (
	<FormYesNoComponent
		formMethods={formMethods}
		name="quote.tListingRequired"
		label="Does this bond require a Treasury Listing?"
		deselectable
	/>
);

type BondFileUploadSectionProps = {
	isBid: boolean;
	filesController: ReturnType<typeof useController<BondRequestFormValues, "quote.files">>;
	isSubmitted: boolean;
	topPadding?: true;
};

const BondFileUploadSection = ({
	isBid,
	filesController,
	isSubmitted,
	topPadding,
}: BondFileUploadSectionProps) => (
	<FormSection
		topPadding={topPadding}
		header={isBid ? "RFP / Bid Package" : "Contract"}
		subHeader={
			isBid
				? "Please upload a copy of the RFP / Bid Package"
				: "Please upload a copy of the contract"
		}
		id="files"
		partial={isSubmitted && (filesController.field.value ?? []).length === 0}
	>
		<FileUpload
			showTabs={false}
			value={filesController.field.value ?? []}
			onChange={filesController.field.onChange}
			onBlur={filesController.field.onBlur}
			enablePDFViewer={false}
			allowDelete={true}
			enableTypeSelection={false}
			allowedTypesAndLabels={
				isBid
					? { [FileType.bid_package]: "RFP / Bid Package" as const }
					: { [FileType.contract]: "Contract" as const }
			}
			maxTypesPerFile={1}
			showErrors={isSubmitted}
			hideChecklist
			className="w-[450px]"
			analyticsContext="submission-upload"
			trackingPrefix="bond-request-page-upload"
		/>
	</FormSection>
);

type MinimalFileUploadSectionProps = {
	classificationPipeline: ReturnType<typeof useFileClassificationPipeline>;
	formMethods: ReturnType<typeof useFormContext<BondRequestFormValues>>;
	bondFormData: BondFormData;
	setBondFormData: (value: BondFormData) => void;
	bondFormTemplates: ReturnType<typeof getFilteredBondFormTemplates>;
	allowedBondFormUploadTypes: ReturnType<typeof getAllowedBondFormUploadTypes>;
};

const MinimalFileUploadSection = ({
	classificationPipeline,
	formMethods,
	bondFormData,
	setBondFormData,
	bondFormTemplates,
	allowedBondFormUploadTypes,
}: MinimalFileUploadSectionProps) => {
	const { files, handleDrop, handleDelete, subscribers } = classificationPipeline;

	const contractSuretyType = useWatch({
		control: formMethods.control,
		name: "quote.contractSuretyType",
	});

	const classifiedTypes = useMemo(() => new Set(files.flatMap((f) => f.types)), [files]);
	const documentFiles = useMemo<DocumentFile[]>(
		() =>
			files.map((f) => ({
				id: f.fileId,
				name: f.name,
				types: f.types,
				status: f.classification.status,
				classificationStatus: f.uploadedFile?.classificationStatus,
			})),
		[files],
	);

	return (
		<>
			{subscribers}

			<DocumentUploadSection
				classifiedTypes={classifiedTypes}
				files={documentFiles}
				onDrop={handleDrop}
				onRemoveFile={handleDelete}
				contractSuretyType={contractSuretyType}
				trackingPrefix="bond-request-page"
				bondForm={{
					value: bondFormData,
					onChange: setBondFormData,
					templates: bondFormTemplates,
					allowedUploadTypes: allowedBondFormUploadTypes,
				}}
			/>

			<FormComponent
				formMethods={formMethods}
				name="quote.additionalInfo.comment"
				label="Submission Narrative"
			>
				<FormComment control={formMethods.control} name="quote.additionalInfo.comment" />
			</FormComponent>
		</>
	);
};
